import { useEffect, useMemo, useState } from 'react';
import { arrowDown, arrowUp, caretDownOutline, caretForwardOutline, sync } from 'ionicons/icons';
import { useActivities } from '@/hooks/useActivities';
import { useNonBusinessDays } from '@/hooks/useNonBusinessDays';
import { useAppQueryParams } from '@/hooks/useAppQueryParams';
import { getMarketClose } from '@/lib/marketClose';
import {
  getCurrencyDisplayClass,
  getCurrencyString,
  getMonthName,
  getTimeFromMilitaryTime,
} from '@/lib/formatting';
import { FundListSortColumn } from '@/types/fund.types';
import type { CustomerActivityListSortAndFilter } from '@/types/fund.types';
import type { ActivityModel } from '@/types/model.types';
import { SortHeader } from '@/components/SortHeader';
import { FundTrades } from '@/components/FundTrades';
import { PageSpinner } from '@/components/PageSpinner';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { useFundListState } from '@/state/FundListState';

/** Port of home.page.ts / home.page.html. */
export default function HomePage() {
  const { activityDate, tenant } = useAppQueryParams();
  // Lives above the routes, so it's still here after a trip to another page.
  const { sortColumn, sortDirection, expandedFundIds, toggleSort, toggleFund } =
    useFundListState();

  const filter: CustomerActivityListSortAndFilter = useMemo(
    () => ({ sortColumn, sortDirection, tradeDateAsYyyyMmDd: activityDate, tenant }),
    [sortColumn, sortDirection, activityDate, tenant],
  );

  const {
    activities,
    dateQueried,
    lastTradeTime,
    isPending,
    isError,
    error,
    isFetching,
    refetch,
  } = useActivities(filter);
  const { data: nonBusinessDays } = useNonBusinessDays();

  if (isPending) return <PageSpinner message="Loading funds..." />;
  if (isError) {
    return <ErrorState title="Could not load funds" error={error} onRetry={() => void refetch()} />;
  }

  return (
    <div className="mx-auto max-w-7xl px-3 pb-8 lg:px-4">
      <StatusBar
        dateQueried={dateQueried}
        lastTradeTime={lastTradeTime}
        nonBusinessDays={nonBusinessDays ?? {}}
        isRefreshing={isFetching}
        onRefresh={() => void refetch()}
      />

      {/* Sticky column headers. size-lg="2" etc. become col-span utilities. */}
      <div className="sticky top-14 z-20 grid grid-cols-12 items-center gap-1 border-b border-line-strong bg-surface py-2 lg:top-[70px] lg:gap-2">
        <div className="col-span-5 lg:col-span-2">
          <SortHeader
            label="Ticker"
            column={FundListSortColumn.TICKER}
            activeColumn={sortColumn}
            direction={sortDirection}
            onToggle={toggleSort}
          />
        </div>
        <div className="hidden lg:col-span-3 lg:block">
          <SortHeader
            label="Fund Name"
            column={FundListSortColumn.FUND_NAME}
            activeColumn={sortColumn}
            direction={sortDirection}
            onToggle={toggleSort}
          />
        </div>
        <div className="col-span-5 flex justify-end lg:col-span-3">
          <SortHeader
            label="Activity"
            column={FundListSortColumn.ACTIVITY}
            activeColumn={sortColumn}
            direction={sortDirection}
            onToggle={toggleSort}
          />
        </div>
        <div className="hidden justify-end lg:col-span-2 lg:flex">
          <SortHeader
            label="Largest Trade"
            column={FundListSortColumn.LARGEST_TRADE}
            activeColumn={sortColumn}
            direction={sortDirection}
            onToggle={toggleSort}
          />
        </div>
        <div className="col-span-2 text-center font-semibold text-content lg:col-span-2">
          <span title="More purchases or sales for the day">Trend</span>
        </div>
      </div>

      {activities.length === 0 ? (
        <p className="py-10 text-center text-muted">No trades received on {dateQueried}</p>
      ) : (
        <ul className="divide-y divide-line">
          {activities.map((activity) => {
            const fundId = activity.fund!.fundId;
            const isOpen = expandedFundIds.has(fundId);

            return (
              <li key={activity.activityId}>
                <div className="grid grid-cols-12 items-center gap-1 py-2 hover:bg-hover lg:gap-2">
                  <div className="col-span-5 flex items-center gap-1 lg:col-span-2">
                    <button
                      type="button"
                      onClick={() => toggleFund(fundId)}
                      aria-expanded={isOpen}
                      aria-label={`${isOpen ? 'Hide' : 'Show'} trades for ${activity.fund!.ticker}`}
                      className="rounded p-1 text-primary hover:bg-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                    >
                      <Icon
                        icon={isOpen ? caretDownOutline : caretForwardOutline}
                        className="size-4"
                      />
                    </button>
                    <span className="font-medium text-content">{activity.fund!.ticker}</span>
                  </div>

                  <div className="hidden truncate text-content-secondary lg:col-span-3 lg:block">
                    {activity.fund!.name}
                  </div>

                  <div className="col-span-5 pr-1 text-right lg:col-span-3">
                    <span className={`${getCurrencyDisplayClass(activity.aggregateAmount)} whitespace-nowrap tabular-nums`}>
                      {getCurrencyString(activity.aggregateAmount)}
                    </span>
                  </div>

                  <div className="hidden text-right lg:col-span-2 lg:block">
                    <span className={`${getCurrencyDisplayClass(activity.largestTradeAmount)} whitespace-nowrap tabular-nums`}>
                      {getCurrencyString(activity.largestTradeAmount)}
                    </span>
                  </div>

                  <div className="col-span-2 flex justify-center lg:col-span-2">
                    <TrendArrow activity={activity} />
                  </div>
                </div>

                {isOpen && (
                  <div className="border-b border-line pb-2">
                    <FundTrades fundId={fundId} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface StatusBarProps {
  dateQueried: string;
  lastTradeTime: string | null;
  nonBusinessDays: Record<string, { date: string; description: string }>;
  isRefreshing: boolean;
  onRefresh: () => void;
}

function StatusBar({
  dateQueried,
  lastTradeTime,
  nonBusinessDays,
  isRefreshing,
  onRefresh,
}: StatusBarProps) {
  // The countdown to close is live, so tick it once a minute.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const marketClose = getMarketClose(now, nonBusinessDays);

  const year = dateQueried ? dateQueried.substring(0, 4) : '';
  const month = dateQueried ? dateQueried.substring(5, 7) : '';
  const day = dateQueried ? dateQueried.substring(8, 10) : '';
  const time = lastTradeTime ? getTimeFromMilitaryTime(lastTradeTime) : '';

  const shortText =
    dateQueried && lastTradeTime ? `${time}, ${month}/${day}/${year}` : `None received on ${dateQueried}`;
  const longText =
    dateQueried && lastTradeTime
      ? `${time}, ${getMonthName(month)} ${day}, ${year}`
      : `No intermediary/platform trades received on ${dateQueried}`;

  return (
    <div className="grid gap-x-4 gap-y-1 py-3 text-sm sm:grid-cols-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          title="Refresh data"
          aria-label="Refresh data"
          className="rounded p-1 text-primary hover:bg-hover disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
          <Icon
            icon={sync}
            className={`size-6 ${isRefreshing ? 'animate-spin motion-reduce:animate-none' : ''}`}
          />
        </button>
        <span className="font-semibold text-content">
          <span className="lg:hidden">Last Trade Rcvd:</span>
          <span className="hidden lg:inline">Last Trade File Received:</span>
        </span>
        <span className="text-content-secondary">
          <span className="lg:hidden">{shortText}</span>
          <span className="hidden lg:inline">{longText}</span>
        </span>
      </div>

      <div className="flex items-center gap-2 sm:justify-end">
        <span className="font-semibold text-content">Market Close:</span>
        <span className="text-content-secondary">{marketClose}</span>
      </div>
    </div>
  );
}

function TrendArrow({ activity }: { activity: ActivityModel }) {
  const up = activity.purchaseCount >= activity.saleCount;
  const label = up ? 'More purchases than sales' : 'More sales than purchases';

  // Was a .ico image, which couldn't change colour for dark mode.
  return (
    <span title={label}>
      <Icon
        icon={up ? arrowUp : arrowDown}
        label={label}
        className={`size-6 ${up ? 'text-money-positive' : 'text-money-negative'}`}
      />
    </span>
  );
}
